import React, { useEffect, useState } from 'react';
import './Dashboard.css';
import NavBar from '../Navbar/Navbar.js';
import Header from '../Header/Header.js';
import { useUser } from '../UserContext.js';
import { useDispatch, useSelector } from 'react-redux';
import { hideLoading, showLoading } from '../store/loadingSlice.js';
import axiosInstance from '../axiosConfig.js';
import * as Fa from 'react-icons/fa';

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

const Card = ({ children, className = '' }) => (
  <div className={`dash-card ${className}`}>{children}</div>
);

const StatCard = ({ title, value, diff, icon }) => {
  const IconComponent = Fa[icon]; 
  return (
    <Card className="stat-card">
      <div className="stat-card-content">
        <div className="stat-icon">
          {IconComponent ? <IconComponent size={24} /> : null} 
        </div>
        <div className="stat-info">
          <div className="stat-title">{title}</div>
          <div className="stat-value">{value}</div>
          {diff && <div className="stat-diff">{diff}</div>}
        </div>
      </div>
    </Card>
  );
};

const SectionHeader = ({ title, subtitle, right }) => (
  <div className="section-header">
    <div>
      <h3 className="section-title">{title}</h3>
      {subtitle && <div className="section-subtitle">{subtitle}</div>}
    </div>
    {right}
  </div>
);

const Dashboard = () => {
  const [isNavOpen, setIsNavOpen] = useState(true);
  const { username, companyName } = useUser();
  const staticData = useSelector(state => state.static.static);
  const modules = useSelector((state) => state.liscence.modules);
  const modulesArray = modules.modules.split(',').map(Number);
  const licenseData = useSelector((state) => state.liscence.modules);
  const dispatch = useDispatch();

  const [boxData, setBoxData] = useState({
    titles: ["", "", "", ""],
    values: ["", "", "", ""],
    icons: ["", "", "", ""]
  });

  const [chartData, setChartData] = useState({
    completedPerMonth: [],
    tasksThisWeek: [],
    title1:'',
    title2:'',
    subTitle1:'',
    subTitle2:'',
    loading: true,
    error: null
  });

  const toggleNav = () => setIsNavOpen((v) => !v);

  // API call for box data
  useEffect(() => {
    const getBoxData = async () => {
      try {
        dispatch(showLoading());

        const thisClientID = localStorage.getItem("token");
        const parameters = {
          url: `https://${licenseData.company_url}/s1services`,
          service: "SqlData",
          clientID: thisClientID,
          appId: licenseData.appid,
          SqlName: "9200",
          param1: licenseData.company_id,
          param2: staticData.actor,
        };

        const response = await axiosInstance.post("/api", parameters);

        if (response.data?.success && response.data.rows?.length > 0) {
          const row = response.data.rows[0];
          setBoxData({
            titles: [
              row.boxTitle1 || "",
              row.boxTitle2 || "",
              row.boxTitle3 || "",
              row.boxTitle4 || "",
            ],
            values: [
              row.boxData1 || "",
              row.boxData2 || "",
              row.boxData3 || "",
              row.boxData4 || "",
            ],
            icons: [
              row.icon1 || "📊",
              row.icon2 || "📊",
              row.icon3 || "📊",
              row.icon4|| "📊"
            ]
          });
        }
      } catch (error) {
        console.error("Error fetching box data:", error);
      } finally {
        dispatch(hideLoading());
      }
    };

    getBoxData();
  }, [dispatch, licenseData]);

  // API call for chart data
  useEffect(() => {
    const getChartsData = async () => {
      try {
        setChartData(prev => ({ ...prev, loading: true, error: null }));
        
        const thisClientID = localStorage.getItem("token");
        const parameters = {
          url: `https://${licenseData.company_url}/s1services/JS/WebappCharts/WebappCharts`,
          clientID: thisClientID,
          vCompany: licenseData.company_id,
          vActor: staticData.actor,
        };

        const response = await axiosInstance.post("/api", parameters);

        if (response.data) {
          setChartData({
            completedPerMonth: response.data.completedProjects || [],
            tasksThisWeek: response.data.weeklyTasks || [],
            title1:response.data.title1 || '',
            title2:response.data.title2 || '',
            subTitle1:response.data.subTitle1 || '',
            subTitle2:response.data.subTitle2 || '',
            loading: false,
            error: null
          });
        }
      } catch (error) {
        console.error("Error fetching chart data:", error);
        setChartData(prev => ({
          ...prev,
          loading: false,
          error: "Failed to load chart data"
        }));
      }
    };

    getChartsData();
  }, [licenseData]);

  return (
    <div className={`dashboard-container ${isNavOpen ? 'nav-open' : 'nav-closed'}`}>
      <NavBar toggleNav={toggleNav} />
      <div className="dashboard-content">
        <Header username={username} companyName={companyName} />
        
        {/* KPI Boxes from API */}
        {modulesArray.includes(14) && (
          <div className="cards-grid">
            <StatCard 
              title={boxData.titles[0]} 
              value={boxData.values[0]} 
              icon={boxData.icons[0]} 
            />
            <StatCard 
              title={boxData.titles[1]} 
              value={boxData.values[1]} 
              icon={boxData.icons[1]} 
            />
            <StatCard 
              title={boxData.titles[2]} 
              value={boxData.values[2]} 
              icon={boxData.icons[2]} 
            />
            <StatCard 
              title={boxData.titles[3]} 
              value={boxData.values[3]} 
              icon={boxData.icons[3]} 
            />
          </div>
        )}

        {/* Charts */}
        {modulesArray.includes(15) && (
          <div className="charts-grid">
            <Card>
              <SectionHeader title={chartData.title1} subtitle={chartData.subTitle1} />
              <div className="chart-container">
                {chartData.loading ? (
                  <div className="chart-loading">Loading chart data...</div>
                ) : chartData.error ? (
                  <div className="chart-error">{chartData.error}</div>
                ) : chartData.completedPerMonth.length > 0 ? (
                  <ResponsiveContainer width="85%" height="100%">
                    <BarChart data={chartData.completedPerMonth}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fontSize: 12 }} 
                        interval={0} 
                        angle={-15} 
                        textAnchor="end" 
                        height={60} 
                      />
                      <YAxis />
                      <Tooltip />
                      <Bar 
                        dataKey="completed" 
                        radius={[6, 6, 0, 0]} 
                        fill="#3b82f6" 
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="chart-no-data">No data available</div>
                )}
              </div>
            </Card>

            <Card>
              <SectionHeader title={chartData.title2} subtitle={chartData.subTitle2} />
              <div className="chart-container">
                {chartData.loading ? (
                  <div className="chart-loading">Loading chart data...</div>
                ) : chartData.error ? (
                  <div className="chart-error">{chartData.error}</div>
                ) : chartData.tasksThisWeek.length > 0 ? (
                  <ResponsiveContainer width="85%" height="100%">
                    <LineChart data={chartData.tasksThisWeek}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Line 
                        type="monotone" 
                        dataKey="tasks" 
                        strokeWidth={2} 
                        stroke="#22c55e" 
                        dot 
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="chart-no-data">No data available</div>
                )}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;